/* Shofaat theme — vanilla JS, no dependencies. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const T = window.theme || {};
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Money ---------- */
  function formatMoney(cents, format = T.moneyFormat || '₪{{amount}}') {
    const value = Number(cents) || 0;
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      const [i, f] = (n / 100).toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (f ? dec + f : '');
    };
    const key = (format.match(re) || [])[1];
    const out = {
      amount: fmt(value, 2).replace(/\.00$/, ''),
      amount_no_decimals: fmt(value, 0),
      amount_with_comma_separator: fmt(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(value, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(value, 2, "'", '.'),
    }[key] || fmt(value, 2);
    return format.replace(re, out);
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2800);
  }

  /* ---------- Drawers ---------- */
  const Drawer = {
    open(id, opener) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      clearTimeout(drawer._hideTimer);
      drawer.hidden = false;
      drawer._opener = opener || document.activeElement;
      requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
      document.body.classList.add('has-overlay');
      setTimeout(() => $('.drawer__panel', drawer)?.focus({ preventScroll: true }), 60);
    },
    close(drawer) {
      if (!drawer || drawer.hidden) return;
      drawer.classList.remove('is-open');
      if (!$$('[data-drawer].is-open').length) document.body.classList.remove('has-overlay');
      drawer._hideTimer = setTimeout(() => { drawer.hidden = true; }, 380);
      drawer._opener?.focus?.({ preventScroll: true });
    },
    closeAll() { $$('[data-drawer].is-open').forEach((d) => Drawer.close(d)); },
  };

  /* ---------- Cart ---------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page) ids.push(page.dataset.section);
      return ids;
    },
    async request(url, body, isForm) {
      const res = await fetch(url, {
        method: 'POST',
        headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.cartError);
      return data;
    },
    async add(form) {
      const fd = new FormData(form);
      fd.append('sections', this.sectionIds().join(','));
      fd.append('sections_url', location.pathname);
      const data = await this.request(T.routes.cartAdd, fd, true);
      this.render(data.sections);
      return data;
    },
    async addItems(items) {
      const data = await this.request(T.routes.cartAdd, JSON.stringify({
        items: items.map((i) => ({ id: i.id, quantity: 1 })), sections: this.sectionIds(), sections_url: location.pathname,
      }));
      this.render(data.sections);
      return data;
    },
    async change(key, quantity) {
      const data = await this.request(T.routes.cartChange, JSON.stringify({
        id: key, quantity, sections: this.sectionIds(), sections_url: location.pathname,
      }));
      this.render(data.sections);
      return data;
    },
    render(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        const fresh = $('[data-cart-inner]', parseHTML(sections['cart-drawer']));
        const current = $('[data-cart-inner]');
        if (fresh && current) current.replaceWith(fresh);
        const count = Number(fresh?.dataset.count || 0);
        $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
        $$('.cart-btn').forEach((btn) => { btn.classList.remove('is-bump'); void btn.offsetWidth; btn.classList.add('is-bump'); });
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.section]) {
        const fresh = $('[data-cart-page-inner]', parseHTML(sections[page.dataset.section]));
        const current = $('[data-cart-page-inner]', page);
        if (fresh && current) current.replaceWith(fresh);
      }
    },
  };

  /* ---------- Global click delegation ---------- */
  document.addEventListener('click', async (e) => {
    const t = e.target;

    const opener = t.closest('[data-open-drawer]');
    if (opener) { e.preventDefault(); Drawer.open(opener.dataset.openDrawer, opener); return; }

    const cartOpener = t.closest('[data-open-cart]');
    if (cartOpener && !document.body.classList.contains('template-cart')) { e.preventDefault(); Drawer.open('CartDrawer', cartOpener); return; }

    const closer = t.closest('[data-close-drawer]');
    if (closer) { e.preventDefault(); Drawer.close(closer.closest('[data-drawer]')); return; }

    const modalOpener = t.closest('[data-open-modal]');
    if (modalOpener) {
      const dialog = document.getElementById(modalOpener.dataset.openModal);
      if (dialog?.showModal) { e.preventDefault(); dialog.showModal(); $('input', dialog)?.focus(); }
      return;
    }
    const modalCloser = t.closest('[data-close-modal]');
    if (modalCloser) { modalCloser.closest('dialog')?.close(); return; }
    if (t.tagName === 'DIALOG' && t.open) { t.close(); return; } // click on backdrop

    const lineBtn = t.closest('[data-line-change]');
    if (lineBtn) {
      e.preventDefault();
      const line = lineBtn.closest('.line');
      line?.classList.add('is-busy');
      try { await Cart.change(lineBtn.dataset.lineChange, Number(lineBtn.dataset.qty)); }
      catch (err) { toast(err.message); line?.classList.remove('is-busy'); }
      return;
    }

    const jump = t.closest('[data-jump]');
    if (jump) {
      const target = $(jump.dataset.jump);
      if (!target) return;
      e.preventDefault();
      if (jump.hasAttribute('data-open-details') && target.tagName === 'DETAILS') target.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const swap = t.closest('.swatch-dot');
    if (swap) {
      const card = swap.closest('[data-card]');
      const img = card && $('.card__img:not(.card__img--hover)', card);
      if (img && swap.dataset.swap) { img.removeAttribute('srcset'); img.src = swap.dataset.swap; }
      $$('.swatch-dot', card).forEach((d) => d.classList.toggle('is-active', d === swap));
      return;
    }

    const more = t.closest('[data-reviews-more]');
    if (more) {
      $$('[data-review].is-hidden', more.closest('section')).forEach((r) => r.classList.remove('is-hidden'));
      more.remove();
      return;
    }

    if (t.closest('[data-to-top]')) window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Drawer.closeAll(); });

  /* ---------- Add to cart ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const buttons = [...$$('[data-add-button]', form), ...(form.id ? $$(`[data-add-button][form="${form.id}"]`) : [])];
    if (buttons.some((b) => b.disabled)) return;
    const error = $('[data-form-error]', form);
    if (error) error.hidden = true;
    buttons.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); });
    try {
      await Cart.add(form);
      buttons.forEach((b) => {
        const label = $('[data-add-label]', b);
        if (!label) return;
        const original = label.textContent;
        b.classList.add('is-added');
        label.textContent = T.strings.added;
        setTimeout(() => { b.classList.remove('is-added'); label.textContent = original; }, 1600);
      });
      if (!document.body.classList.contains('template-cart')) Drawer.open('CartDrawer');
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; } else toast(err.message);
    } finally {
      buttons.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-autosubmit]')) e.target.form?.submit();
  });

  /* ---------- Shop the look ---------- */
  function lookItems(look) {
    const out = [];
    $$('[data-look-item]', look).forEach((li) => {
      const include = $('[data-look-include]', li);
      if (include && !include.checked) return;
      const v = $('[data-look-variant]', li);
      if (!v) return;
      const price = v.tagName === 'SELECT' ? v.selectedOptions[0]?.dataset.price : v.dataset.price;
      out.push({ id: Number(v.value), price: Number(price) || 0 });
    });
    return out;
  }
  function updateLook(look) {
    $$('[data-look-item]', look).forEach((li) => {
      const sel = $('select[data-look-variant]', li);
      const label = $('[data-look-price]', li);
      if (sel && label) label.innerHTML = formatMoney(sel.selectedOptions[0]?.dataset.price);
      const include = $('[data-look-include]', li);
      li.classList.toggle('is-off', !!include && !include.checked);
    });
    const items = lookItems(look);
    const total = $('[data-look-total]', look);
    if (total) total.innerHTML = formatMoney(items.reduce((sum, i) => sum + i.price, 0));
    const btn = $('[data-look-add]', look);
    if (btn) btn.disabled = items.length === 0;
  }
  document.addEventListener('change', (e) => {
    const look = e.target.closest('[data-look]');
    if (look) updateLook(look);
  });
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-look-add]');
    if (!btn) return;
    const look = btn.closest('[data-look]');
    const items = lookItems(look);
    if (!items.length) return;
    btn.classList.add('is-loading');
    try {
      await Cart.addItems(items);
      if (!document.body.classList.contains('template-cart')) Drawer.open('CartDrawer');
    } catch (err) { toast(err.message); }
    finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Product page ---------- */
  function scrollTrackTo(track, slide, smooth = true) {
    if (!track || !slide) return;
    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' });
  }

  function initGallery(root) {
    const track = $('[data-gallery-track]', root);
    if (!track) return;
    const slides = $$('.gallery__slide', track);
    const indexEl = $('[data-gallery-index]', root);
    const thumbs = $$('[data-thumb]', root);
    let current = 0;
    const setActive = (i) => {
      current = i;
      if (indexEl) indexEl.textContent = i + 1;
      const id = slides[i]?.dataset.mediaId;
      thumbs.forEach((th) => {
        const on = th.dataset.thumb === id;
        th.classList.toggle('is-active', on);
        if (on) th.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) setActive(slides.indexOf(en.target)); });
    }, { root: track, threshold: 0.6 });
    slides.forEach((s) => io.observe(s));
    thumbs.forEach((th) => th.addEventListener('click', () => scrollTrackTo(track, slides.find((s) => s.dataset.mediaId === th.dataset.thumb))));
    $('[data-gallery-prev]', root)?.addEventListener('click', () => scrollTrackTo(track, slides[Math.max(0, current - 1)]));
    $('[data-gallery-next]', root)?.addEventListener('click', () => scrollTrackTo(track, slides[Math.min(slides.length - 1, current + 1)]));
    root._goToMedia = (mediaId) => {
      const slide = slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) scrollTrackTo(track, slide);
    };
  }

  function initProduct(root) {
    initGallery(root);
    const picker = $('[data-variant-picker]', root);
    const jsonEl = $('[data-product-json]', root);
    const form = $('form[data-product-form]', root);
    const idInput = form && $('[data-variant-id]', form);
    const sticky = $('[data-sticky-buy]', root);

    if (picker && jsonEl) {
      const product = JSON.parse(jsonEl.textContent);
      const fieldsets = $$('fieldset[data-option-index]', picker);
      const selected = () => fieldsets.map((fs) => $('input:checked', fs)?.value);
      const find = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

      const markAvailability = (opts) => {
        fieldsets.forEach((fs, idx) => {
          $$('input', fs).forEach((input) => {
            const test = [...opts];
            test[idx] = input.value;
            const v = find(test);
            input.classList.toggle('is-unavailable', !v || !v.available);
          });
        });
      };

      const update = () => {
        const opts = selected();
        const variant = find(opts);
        fieldsets.forEach((fs, i) => { const l = $('[data-option-label]', fs); if (l) l.textContent = opts[i] || ''; });
        markAvailability(opts);
        if (variant && idInput) idInput.value = variant.id;

        // price
        const priceBox = $('.buybox__price', root);
        if (priceBox && variant) {
          const onSale = variant.compare_at_price > variant.price;
          const priceEl = $('[data-price]', priceBox);
          priceEl?.classList.toggle('price--sale', onSale);
          const now = $('[data-price-now]', priceBox);
          if (now) now.innerHTML = formatMoney(variant.price);
          const was = $('[data-price-was]', priceBox);
          if (was) { was.hidden = !onSale; was.innerHTML = onSale ? formatMoney(variant.compare_at_price) : ''; }
          const badge = $('[data-price-badge]', priceBox);
          const save = $('[data-price-save]', priceBox);
          const bundlePill = $('[data-bundle-pill]', root);
          if (onSale) {
            const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
            if (badge) { badge.hidden = false; const bt = $('[data-price-badge-text]', badge); if (bt) bt.textContent = (badge.dataset.saleText || 'חסכי {pct}% בהזמנה היום').replace('{pct}', pct).replace('{amount}', ((variant.compare_at_price - variant.price) / 100).toFixed(2).replace(/\.00$/, '') + ' ₪'); }
            if (save && !save.hasAttribute('data-off')) { save.hidden = false; save.innerHTML = T.strings.youSave.replace('[amount]', formatMoney(variant.compare_at_price - variant.price)); }
            if (bundlePill) bundlePill.hidden = true;
          } else {
            if (badge) badge.hidden = true;
            if (save) save.hidden = true;
            if (bundlePill) bundlePill.hidden = false;
          }
        }

        // bundle unit prices
        if (variant) $$('[data-bundle-unit]', root).forEach((el) => {
          el.innerHTML = formatMoney(Math.round((variant.price * (100 - Number(el.dataset.pct))) / 100));
        });

        // buttons
        $$('[data-add-button]', root).forEach((btn) => {
          const label = $('[data-add-label]', btn);
          const ok = variant && variant.available;
          btn.disabled = !ok;
          if (label) label.textContent = ok ? (btn.dataset.labelAvailable || T.strings.addToCart) : (variant ? T.strings.soldOut : T.strings.unavailable);
        });

        // media + sticky + url
        if (variant?.featured_media) {
          root._goToMedia?.(variant.featured_media.id);
          const sImg = sticky && $('.sticky-buy__img', sticky);
          const src = variant.featured_media.preview_image?.src;
          if (sImg && src) { sImg.removeAttribute('srcset'); sImg.src = `${src}${src.includes('?') ? '&' : '?'}width=120`; }
        }
        if (sticky && variant) {
          const p = $('[data-sticky-price]', sticky); if (p) p.innerHTML = formatMoney(variant.price);
          const vt = $('[data-sticky-variant]', sticky); if (vt) vt.textContent = variant.title;
        }
        const lookCurrent = $('[data-look-current]', root);
        if (lookCurrent && variant) {
          lookCurrent.value = variant.id;
          lookCurrent.dataset.price = variant.price;
          const lp = $('[data-look-price]', lookCurrent.closest('[data-look-item]'));
          if (lp) lp.innerHTML = formatMoney(variant.price);
          $$('[data-look]', root).forEach(updateLook);
        }
        try { if (variant && root.dataset.productUrl) history.replaceState(null, '', `${root.dataset.productUrl}?variant=${variant.id}`); } catch (e) { /* file:// previews */ }
      };

      picker.addEventListener('change', update);
      markAvailability(selected());
      root._selectOption = (name, value) => {
        const fs = fieldsets.find((f) => f.querySelector('.opt__legend')?.textContent.trim().startsWith(name));
        const input = fs && $$('input', fs).find((i) => i.value === value);
        if (input) { input.checked = true; update(); return fs; }
        return null;
      };
    }

    // sticky add to cart
    const mainBuy = $('[data-main-buy]', root);
    if (sticky && mainBuy) {
      new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        $('button', sticky).tabIndex = show ? 0 : -1;
        document.body.classList.toggle('has-sticky-buy', show);
      }).observe(mainBuy);
    }
  }

  /* ---------- Size finder ---------- */
  // Works on the store scale "3XL (46)": estimates an EU number, then picks the closest size this product offers.
  function initFinder(el) {
    const form = $('[data-finder-form]', el);
    const result = $('[data-finder-result]', el);
    const sizes = (el.dataset.sizes || '').split(',').map((s) => s.trim()).filter(Boolean);
    const euOf = (label, i) => {
      const t = label.toUpperCase().replace(/\s+/g, '');
      const eu = t.match(/(?:^|[^\d])(3[4-9]|[4-6]\d)(?:[^\d]|$)/);           // explicit EU / Israeli number
      if (eu) return Number(eu[1]);
      const n = t.match(/^(\d)X{1,2}L?/) || t.match(/^(\d)XL/);                 // 2XL, 3X, 4XL
      if (n) return 40 + 2 * Number(n[1]);
      const x = t.match(/^(X+)L/);                                              // XL, XXL, XXXL
      if (x) return 40 + 2 * x[1].length;
      const base = { XS: 34, S: 36, M: 38, L: 40 }[t];
      return base || 42 + 2 * i;
    };
    const scale = sizes.map((label, i) => ({ label, eu: euOf(label, i) }));
    let recommended = null;
    const band = (val, limits) => { const i = limits.findIndex((l) => val <= l); return 34 + 2 * (i === -1 ? limits.length : i); }; // XS (34) … 7XL (54)

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const h = Number(form.height.value), w = Number(form.weight.value), b = Number(form.bust.value);
      const fit = Number(form.querySelector('input[name="fit"]:checked')?.value || 0);
      const err = $('[data-finder-error]', el);
      if (!h || !w || !scale.length) { err.hidden = false; return; }
      err.hidden = true;
      let eu = band(w, [50, 57, 64, 71, 78, 88, 101, 112, 122, 133]); // weight → EU 34…54
      if (h < 155) eu += 2; else if (h > 178) eu -= 2;         // shorter frame = fuller measurements
      if (b) eu = Math.round(((band(b, [84, 88, 92, 96, 100, 105, 110, 116, 122, 128]) * 2 + eu) / 3) / 2) * 2; // bust is the stronger signal
      eu += fit * 2;
      const pick = scale.reduce((best, s) => (Math.abs(s.eu - eu) < Math.abs(best.eu - eu) || (Math.abs(s.eu - eu) === Math.abs(best.eu - eu) && s.eu > best.eu) ? s : best), scale[0]);
      recommended = pick.label;
      $('[data-finder-size]', el).textContent = recommended;
      const fitText = { '-1': ', בגזרה צמודה', 0: '', 1: ', בגזרה משוחררת' }[fit];
      const edge = eu > scale[scale.length - 1].eu ? ' — זו המידה הכי גדולה בדגם הזה, כדאי להציץ בטבלת המידות' : '';
      $('[data-finder-why]', el).textContent = `לפי גובה ${h} ס״מ ומשקל ${w} ק״ג${b ? ` והיקף חזה ${b} ס״מ` : ''}${fitText}${edge}`;
      result.hidden = false;
    });

    $('[data-finder-apply]', el)?.addEventListener('click', () => {
      const pdp = $('[data-pdp]');
      const fs = recommended && pdp?._selectOption?.(el.dataset.sizeOption || 'מידה', recommended);
      if (fs) fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ---------- Slideshow ---------- */
  function initSlideshow(el) {
    const track = $('[data-slides]', el);
    const slides = $$('.hero__slide', track);
    if (slides.length < 2) return;
    const dots = $$('[data-slide-dot]', el);
    const rtl = getComputedStyle(track).direction === 'rtl';
    let index = 0, timer;
    const go = (i) => {
      index = (i + slides.length) % slides.length;
      track.scrollTo({ left: (rtl ? -1 : 1) * index * track.clientWidth, behavior: 'smooth' });
    };
    track.addEventListener('scroll', () => {
      const i = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
      if (i !== index) index = i;
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    }, { passive: true });
    dots.forEach((d) => d.addEventListener('click', () => { go(Number(d.dataset.slideDot)); restart(); }));
    $('[data-slide-prev]', el)?.addEventListener('click', () => { go(index - 1); restart(); });
    $('[data-slide-next]', el)?.addEventListener('click', () => { go(index + 1); restart(); });
    const start = () => { if (el.dataset.autoplay === 'true') timer = setInterval(() => go(index + 1), Number(el.dataset.interval) || 6000); };
    const restart = () => { clearInterval(timer); start(); };
    el.addEventListener('pointerenter', () => clearInterval(timer));
    el.addEventListener('pointerleave', restart);
    el.addEventListener('touchstart', () => clearInterval(timer), { passive: true });
    start();
  }

  /* ---------- Small widgets ---------- */
  function initRotator(el) {
    const items = $$('.announce__item', el);
    if (items.length < 2) return;
    let i = 0;
    setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, Number(el.dataset.interval) || 4000);
  }

  function initCountdown(el) {
    const end = new Date(el.dataset.countdown).getTime();
    if (!end || Number.isNaN(end) || end <= Date.now()) return;
    el.hidden = false;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      let s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (!s) { el.hidden = true; clearInterval(timer); return; }
      const d = Math.floor(s / 86400); s %= 86400;
      const h = Math.floor(s / 3600); s %= 3600;
      const m = Math.floor(s / 60); s %= 60;
      $('[data-cd="d"]', el).textContent = pad(d);
      $('[data-cd="h"]', el).textContent = pad(h);
      $('[data-cd="m"]', el).textContent = pad(m);
      $('[data-cd="s"]', el).textContent = pad(s);
    };
    const timer = setInterval(tick, 1000);
    tick();
  }

  async function initRecommendations(el) {
    if (!el.dataset.url || el.children.length) return;
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const fresh = $('[data-recommendations]', parseHTML(html));
      if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
    } catch (e) { /* recommendations are optional */ }
  }

  // automatic "complete the look": fetch related products, keep the complementary kind (top ↔ bottom)
  async function initAutoLook(look) {
    try {
      const html = await (await fetch(look.dataset.url)).text();
      const doc = parseHTML(html);
      const picks = $$('template[data-kind]', doc).filter((t) => t.dataset.kind === look.dataset.want).slice(0, 2);
      if (!picks.length) return;
      const list = $('[data-look-list]', look);
      const thumbs = $('[data-look-thumbs]', look);
      picks.forEach((t) => {
        list.insertAdjacentHTML('beforeend', t.innerHTML);
        if (thumbs && t.dataset.thumb) thumbs.insertAdjacentHTML('beforeend', `<img src="${t.dataset.thumb}" alt="" width="34" height="34" loading="lazy">`);
      });
      const count = $('[data-look-count]', look);
      if (count) count.textContent = picks.length;
      look.hidden = false;
      updateLook(look);
    } catch (e) { /* optional */ }
  }

  function initVideos(el) {
    const vids = $$('video', el);
    if (!('IntersectionObserver' in window)) { vids.forEach((v) => v.play().catch(() => {})); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.preload = 'auto'; en.target.play().catch(() => {}); } else en.target.pause();
    }), { threshold: 0.5 });
    vids.forEach((v) => { v.muted = true; v.playsInline = true; io.observe(v); });
  }

  function initToTop() {
    const btn = $('[data-to-top]');
    if (!btn) return;
    const onScroll = () => { btn.hidden = window.scrollY < 900; };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initFooter() {
    if (window.matchMedia('(max-width: 899px)').matches) $$('[data-footer-col]').forEach((d) => d.removeAttribute('open'));
  }

  /* ---------- Boot ---------- */
  function boot() {
    $$('[data-pdp]').forEach(initProduct);
    $$('[data-size-finder]').forEach(initFinder);
    $$('[data-slideshow]').forEach(initSlideshow);
    $$('[data-rotator]').forEach(initRotator);
    $$('[data-countdown]').forEach(initCountdown);
    $$('[data-recommendations]').forEach(initRecommendations);
    $$('[data-look]').forEach(updateLook);
    $$('[data-look-auto]').forEach(initAutoLook);
    $$('[data-vids]').forEach(initVideos);
    initToTop();
    initFooter();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    const s = e.target;
    $$('[data-pdp]', s).forEach(initProduct);
    $$('[data-size-finder]', s).forEach(initFinder);
    $$('[data-slideshow]', s).forEach(initSlideshow);
    $$('[data-rotator]', s).forEach(initRotator);
    $$('[data-countdown]', s).forEach(initCountdown);
  });

  window.ShofaatCart = Cart;
})();
